// src/_API/axiosConfig.js
import axios from "axios";
import { Capacitor } from "@capacitor/core";
import {
  clearAuthStorage,
  getAuthPersistence,
  getStoredAccessToken,
  getStoredRefreshToken,
  safeGetAuthToken,
  storeAuthTokens,
} from "../Helpers/authStorage";

/**
 * Base URL strategy:
 * 1) Use REACT_APP_API_URL if provided (preferred).
 * 2) If running inside a native Capacitor shell, fallback to production API.
 * 3) Otherwise (web/local), fallback to local dev API.
 *
 * Notes:
 * - We normalize baseURL to avoid double slashes.
 * - We log baseURL once on boot, and optionally each request (in non-production).
 */
const FALLBACK_PUBLIC_API = "https://api.wamsoluciones.com.ar";
const LOCAL_DEFAULT_API = "http://127.0.0.1:8000";

const envBaseURL = (process.env.REACT_APP_API_URL || "").trim();
const resolved =
  envBaseURL ||
  (Capacitor?.isNativePlatform?.() ? FALLBACK_PUBLIC_API : LOCAL_DEFAULT_API);

// normalize: no trailing slash
const baseURL = String(resolved || "").replace(/\/+$/, "");

// apply defaults
axios.defaults.baseURL = baseURL;
// IMPORTANT: do NOT force Content-Type globally; let Axios set it per request.
// axios.defaults.headers.post["Content-Type"] = "application/json";

if (typeof window !== "undefined") {
  const meta = {
    envBaseURL: envBaseURL || null,
    resolvedBaseURL: baseURL,
    isNative: !!Capacitor?.isNativePlatform?.(),
  };
  // eslint-disable-next-line no-console
  console.info("[axiosConfig] Base URL configurada", meta);
}

const AUTH_REFRESH_SKIP_ENDPOINTS = [
  "/user/login/",
  "/auth/propietario/login-pin/",
  "/auth/propietario/request-otp/",
  "/auth/propietario/verify-otp/",
  "/auth/propietario/set-pin/",
  "/auth/proveedor/login/",
  "/auth/proveedor/request-otp/",
  "/auth/proveedor/verify-otp/",
  "/auth/proveedor/set-pin/",
  "/api/token/",
  "/api/token/refresh/",
];

const shouldSkipAuthRefresh = (config) => {
  if (!config) return false;
  if (config.skipAuthRefresh) return true;

  const rawUrl = config.url || "";
  if (!rawUrl) return false;

  let pathname = rawUrl;

  // If absolute URL, extract pathname safely
  if (/^https?:\/\//i.test(rawUrl)) {
    try {
      pathname = new URL(rawUrl).pathname;
    } catch {
      pathname = rawUrl;
    }
  }

  if (!pathname.startsWith("/")) pathname = `/${pathname}`;
  const normalized = pathname.split("?")[0];

  return AUTH_REFRESH_SKIP_ENDPOINTS.some((endpoint) =>
    normalized.endsWith(endpoint)
  );
};

let refreshPromise = null;
let refreshQueue = [];

const enqueueTokenRefresh = () =>
  new Promise((resolve, reject) => {
    refreshQueue.push({ resolve, reject });

    if (!refreshPromise) {
      refreshPromise = performTokenRefresh()
        .then((token) => {
          refreshQueue.forEach((subscriber) => subscriber.resolve(token));
          refreshQueue = [];
          return token;
        })
        .catch((error) => {
          refreshQueue.forEach((subscriber) => subscriber.reject(error));
          refreshQueue = [];
          throw error;
        })
        .finally(() => {
          refreshPromise = null;
        });
    }
  });

// Refresh explícito para quien no pasa por el interceptor (conexiones SSE con el
// token en la URL). Comparte la cola con el interceptor; falla si el refresh
// venció o es inválido.
export const refreshAccessToken = () => enqueueTokenRefresh();

const performTokenRefresh = async () => {
  const refreshToken = await getStoredRefreshToken();
  if (!refreshToken) throw new Error("No refresh token available");

  const persist = await getAuthPersistence();

  const response = await axios.post(
    "/api/token/refresh/",
    { refresh: refreshToken },
    { skipAuthRefresh: true }
  );

  const newAccess = response.data?.access;
  const newRefresh = response.data?.refresh || refreshToken;

  if (!newAccess) {
    throw new Error("Refresh endpoint did not return access token");
  }

  const existingEntry = safeGetAuthToken();
  const persistedMetadata = existingEntry?.metadata || null;

  await storeAuthTokens({
    accessToken: newAccess,
    refreshToken: newRefresh,
    persistSession: persist,
    metadata: persistedMetadata,
  });

  return newAccess;
};

/**
 * Request interceptor:
 * - Adds Authorization header when missing.
 * - (Optional) logs request config in non-production to debug Android "Network Error".
 */
if (!axios.__authRequestInterceptorRegistered) {
  axios.interceptors.request.use(
    async (config) => {
      if (config?.skipAuthRefresh) return config;

      config.headers = config.headers || {};

      if (!config.headers.Authorization) {
        const token = await getStoredAccessToken();
        if (token) config.headers.Authorization = `Bearer ${token}`;
      }

      // Optional request log (very helpful on Android builds)
      if (typeof window !== "undefined" && process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.debug("[axios] →", {
          method: (config.method || "GET").toUpperCase(),
          url: config.url,
          baseURL: config.baseURL || axios.defaults.baseURL,
          isNative: !!Capacitor?.isNativePlatform?.(),
          hasAuth: !!config.headers.Authorization,
        });
      }

      return config;
    },
    (error) => Promise.reject(error)
  );

  axios.__authRequestInterceptorRegistered = true;
}

/**
 * Refresh interceptor:
 * - On 401, tries refresh unless endpoint is in skip list.
 * - Prevents recursion with _retry and skipAuthRefresh.
 */
if (!axios.__authRefreshInterceptorRegistered) {
  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error?.config;
      const status = error?.response?.status;

      if (
        !originalRequest ||
        shouldSkipAuthRefresh(originalRequest) ||
        originalRequest._retry ||
        status !== 401
      ) {
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      try {
        const newToken = await enqueueTokenRefresh();
        if (newToken) {
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
        }
        return axios(originalRequest);
      } catch (refreshError) {
        await clearAuthStorage();
        if (typeof window !== "undefined") {
          window.dispatchEvent(new CustomEvent("auth:logout"));
        }
        return Promise.reject(refreshError);
      }
    }
  );

  axios.__authRefreshInterceptorRegistered = true;
}

/**
 * Period-locked interceptor:
 * - Normalizes 423 payload so UI can show the reason and centralize handling.
 */
if (!axios.__periodLockedInterceptorRegistered) {
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error?.response?.status;
      const payload = error?.response?.data;

      if (status === 423 && payload?.error?.code === "period_locked") {
        const detail = payload.error;
        if (error?.response) {
          error.response.data =
            detail?.message || "El período está bloqueado para modificaciones.";
        }
        error.periodLockedDetail = detail;

        if (typeof window !== "undefined") {
          window.dispatchEvent(new CustomEvent("periodLocked:trigger", { detail }));
        }
      }

      return Promise.reject(error);
    }
  );

  axios.__periodLockedInterceptorRegistered = true;
}

export default axios;
